import { Github, Linkedin, Mail, Send } from 'lucide-react';
import { EmptyState, Skeleton } from '../../../components';
import { useLang } from '../../../i18n';
import type { TeamMember } from '../../../types';
import './TeamSection.scss';

export interface TeamSectionProps {
  team: TeamMember[];
  loading: boolean;
}

function tgUrl(handle: string): string {
  const v = handle.trim();
  if (/^https?:\/\//i.test(v)) return v;
  return `https://t.me/${v.replace(/^@/, '')}`;
}

function MemberCard({ m, size }: { m: TeamMember; size: 'lg' | 'md' }) {
  const avatarBg = m.accent_color || 'var(--c-brand-50)';
  const avatarColor = m.accent_color ? '#fff' : 'var(--c-brand)';
  return (
    <article
      className={`card-frame card-hover about-team__card about-team__card--${size}`}
    >
      <div className="about-team__avatar-wrap">
        {m.avatar_url ? (
          <img
            className="about-team__avatar-img"
            src={m.avatar_url}
            alt={m.full_name}
            loading="lazy"
          />
        ) : (
          <span
            className="about-team__avatar-fallback"
            style={{ background: avatarBg, color: avatarColor }}
            aria-hidden="true"
          >
            {m.initials}
          </span>
        )}
      </div>
      <div className="about-team__body">
        <h3 className="about-team__name">{m.full_name}</h3>
        <span className="tech-label about-team__role">{m.role}</span>
        {m.bio ? <p className="about-team__bio">{m.bio}</p> : null}
        <div className="about-team__socials">
          {m.telegram ? (
            <a
              className="about-team__soc about-team__soc--tg"
              href={tgUrl(m.telegram)}
              target="_blank"
              rel="noreferrer noopener"
              aria-label={`Telegram ${m.full_name}`}
            >
              <Send size={16} />
            </a>
          ) : null}
          {m.linkedin ? (
            <a
              className="about-team__soc about-team__soc--ln"
              href={m.linkedin}
              target="_blank"
              rel="noreferrer noopener"
              aria-label={`LinkedIn ${m.full_name}`}
            >
              <Linkedin size={16} />
            </a>
          ) : null}
          {m.github ? (
            <a
              className="about-team__soc about-team__soc--gh"
              href={m.github}
              target="_blank"
              rel="noreferrer noopener"
              aria-label={`GitHub ${m.full_name}`}
            >
              <Github size={16} />
            </a>
          ) : null}
          {m.email ? (
            <a
              className="about-team__soc about-team__soc--em"
              href={`mailto:${m.email}`}
              aria-label={`Email ${m.full_name}`}
            >
              <Mail size={16} />
            </a>
          ) : null}
        </div>
      </div>
    </article>
  );
}

export default function TeamSection({ team, loading }: TeamSectionProps) {
  const { t } = useLang();
  const leaders = team.filter((m) => m.is_leadership);
  const regular = team.filter((m) => !m.is_leadership);
  const showSkeleton = loading && team.length === 0;
  const empty = !loading && team.length === 0;

  return (
    <section className="section section--tight about-team">
      <div className="container">
        <div className="section-header about-team__head">
          <span className="eyebrow">{t('about.team.t')}</span>
          <h2 className="about-team__title">{t('about.team.sub')}</h2>
        </div>

        {empty ? (
          <EmptyState title={t('about.empty')} />
        ) : showSkeleton ? (
          <div className="about-team__grid about-team__grid--3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="card-frame about-team__skeleton">
                <Skeleton width={80} height={80} shape="circle" />
                <Skeleton width="70%" height={20} />
                <Skeleton width="50%" height={14} />
                <Skeleton width="100%" height={14} />
              </div>
            ))}
          </div>
        ) : (
          <>
            {leaders.length > 0 ? (
              <div className="about-team__block">
                <h4 className="about-team__group-title">{t('about.team.leadership')}</h4>
                <div className="about-team__grid about-team__grid--2">
                  {leaders.map((m) => (
                    <MemberCard key={m.id} m={m} size="lg" />
                  ))}
                </div>
              </div>
            ) : null}
            {regular.length > 0 ? (
              <div className="about-team__block">
                {leaders.length > 0 ? (
                  <h4 className="about-team__group-title">{t('about.team.contributors')}</h4>
                ) : null}
                <div className="about-team__grid about-team__grid--3">
                  {regular.map((m) => (
                    <MemberCard key={m.id} m={m} size="md" />
                  ))}
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}
